import { Keyboard, Megaphone, User } from 'lucide-react';
import { useState } from 'react';

import { CapabilityGate } from '../components/CapabilityGate';
import { UserAvatar } from '../features/admin/users/UserAvatar';
import {
  NOTIFICATION_CHANNEL,
  resolveNotificationRows,
  roleLabel,
} from '../features/auxiliary/auxiliar-adapter';
import { KeyboardShortcutsModal } from '../features/conversations/components/KeyboardShortcutsModal';
import {
  useNotificationPreferences,
  useSetNotificationPreference,
} from '../hooks/use-notification-preferences';
import { useAuthStore } from '../stores/auth-store';
import { useToastStore } from '../stores/toast-store';

import '../features/auxiliary/auxiliar-2b.css';

/**
 * AuxiliarPage — rota `/auxiliar` (Brief 2B T6 · §11 densidade baixa).
 *
 * ÚLTIMA tela do atendente (fecha o Brief 2B FE 8/8). Utilidades do operador.
 * Tela core (sem gate — D1); cada card respeita a cap da sua fonte.
 *
 * v1 honesta = 3 cards REAIS: Perfil (read, auth-store) · Notificações (toggles
 * que PERSISTEM via PUT, cap notifications-manage) · Atalhos (reusa o
 * KeyboardShortcutsModal existente). Tutoriais/Suporte/Multi-tenant/Turno/Editar
 * perfil OMITIDOS honesto (sem backend — sem dado/affordance fabricados).
 */
export function AuxiliarPage(): JSX.Element {
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  return (
    <div className="flex flex-col h-full" style={{ overflow: 'auto' }}>
      <div className="app-pagehead">
        <div className="app-pagehead__top">
          <h1 className="app-pagehead__title">Auxiliar</h1>
          <span className="app-pagehead__sub">Perfil, notificações e atalhos</span>
        </div>
      </div>

      <div className="aux-page">
        <PerfilCard />
        <CapabilityGate capability="notifications-manage">
          <NotificacoesCard />
        </CapabilityGate>
        <AtalhosCard onOpen={() => setShortcutsOpen(true)} />
      </div>

      <KeyboardShortcutsModal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </div>
  );
}

// ─── Perfil (read-only real — auth-store) ───
function PerfilCard(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  if (user == null) return <></>;

  const fields: { label: string; value: string }[] = [
    { label: 'Nome', value: user.name },
    { label: 'E-mail', value: user.email },
    { label: 'Função', value: roleLabel(user.primaryRole) },
    { label: 'Organização', value: tenant?.name ?? '—' },
  ];

  return (
    <section className="aux-card aux-card--span2">
      <div className="aux-card__head">
        <div className="aux-card__icon">
          <User size={18} />
        </div>
        <div style={{ flex: 1 }}>
          <h2 className="aux-card__title">Perfil</h2>
          <p className="aux-card__sub">Suas informações de operador.</p>
        </div>
      </div>
      <div className="aux-profile">
        <UserAvatar id={user.id} name={user.name} size="md" />
        <div className="aux-profile__fields">
          {fields.map((f) => (
            <div key={f.label}>
              <div className="aux-profile__label">{f.label}</div>
              <div className="aux-profile__value">{f.value}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Notificações (REAL — toggles que persistem) ───
function NotificacoesCard(): JSX.Element {
  const { data, isLoading, isError } = useNotificationPreferences();
  const setPreference = useSetNotificationPreference();
  const push = useToastStore((s) => s.push);

  const rows = resolveNotificationRows(data ?? []);

  const handleToggle = (templateId: string, enabled: boolean): void => {
    setPreference.mutate(
      { channel: NOTIFICATION_CHANNEL, templateId, enabled },
      {
        onError: () => {
          push({ variant: 'error', title: 'Não foi possível salvar a preferência' });
        },
      },
    );
  };

  return (
    <section className="aux-card">
      <div className="aux-card__head">
        <div className="aux-card__icon">
          <Megaphone size={18} />
        </div>
        <div style={{ flex: 1 }}>
          <h2 className="aux-card__title">Notificações</h2>
          <p className="aux-card__sub">Quais eventos te interrompem no app.</p>
        </div>
      </div>

      {isLoading && <div className="aux-card__sub">Carregando preferências…</div>}
      {isError && <div className="aux-card__sub">Não foi possível carregar suas preferências.</div>}

      {!isLoading && !isError && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {rows.map((row) => (
            <label key={row.templateId} className="aux-row" style={{ cursor: 'pointer' }}>
              <div>
                <div className="aux-row__title">{row.label}</div>
                <div className="aux-row__sub">{row.sub}</div>
              </div>
              <input
                type="checkbox"
                className="aux-toggle"
                checked={row.enabled}
                disabled={setPreference.isPending}
                onChange={(e) => handleToggle(row.templateId, e.target.checked)}
              />
            </label>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── Atalhos (reusa o KeyboardShortcutsModal existente) ───
function AtalhosCard({ onOpen }: { onOpen: () => void }): JSX.Element {
  // Só os atalhos REAIS hoje (mesmos do KeyboardShortcutsModal). Os do mockup
  // que não existem (g i / g p / n / e…) são omitidos honesto.
  const shortcuts: { keys: string[]; label: string }[] = [
    { keys: ['Cmd', 'Enter'], label: 'Enviar mensagem' },
    { keys: ['Tab'], label: 'Aceitar sugestão da IA' },
    { keys: ['Esc'], label: 'Recusar sugestão / Limpar rascunho' },
    { keys: ['Cmd', '/'], label: 'Abrir / fechar o painel de atalhos' },
  ];

  return (
    <section className="aux-card">
      <div className="aux-card__head">
        <div className="aux-card__icon">
          <Keyboard size={18} />
        </div>
        <div style={{ flex: 1 }}>
          <h2 className="aux-card__title">Atalhos de teclado</h2>
          <p className="aux-card__sub">Operações rápidas no app.</p>
        </div>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onOpen}>
          Abrir modal completo
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {shortcuts.map((s) => (
          <div key={s.label} className="kbd-row">
            <span className="kbd-row__label">{s.label}</span>
            <span className="kbd-row__keys">
              {s.keys.map((k) => (
                <span key={k} className="kbd">
                  {k}
                </span>
              ))}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
